import { useCallback, useEffect, useState } from "react";

const FAVORITES_STORAGE_KEY = "bnssa-mns:favorite-jobs";
const FAVORITES_EVENT = "bnssa-mns:favorites-changed";

const readFavorites = (): string[] => {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(FAVORITES_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((id) => typeof id === "string") : [];
  } catch {
    return [];
  }
};

const writeFavorites = (ids: string[]) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(ids));
    window.dispatchEvent(new CustomEvent(FAVORITES_EVENT, { detail: ids }));
  } catch {
    // silent
  }
};

/**
 * Hook centralisé pour la gestion des offres favorites stockées en localStorage.
 * Synchronisation cross-composants via un CustomEvent global.
 */
export function useFavoriteJobs() {
  const [favorites, setFavorites] = useState<string[]>(() => readFavorites());

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<string[]>).detail;
      if (Array.isArray(detail)) {
        setFavorites(detail);
      } else {
        setFavorites(readFavorites());
      }
    };
    // Sync entre composants montés
    window.addEventListener(FAVORITES_EVENT, handler as EventListener);
    // Sync entre onglets du navigateur
    const storageHandler = (e: StorageEvent) => {
      if (e.key === FAVORITES_STORAGE_KEY) {
        setFavorites(readFavorites());
      }
    };
    window.addEventListener("storage", storageHandler);

    return () => {
      window.removeEventListener(FAVORITES_EVENT, handler as EventListener);
      window.removeEventListener("storage", storageHandler);
    };
  }, []);

  const isFavorite = useCallback(
    (jobId: string) => favorites.includes(jobId),
    [favorites]
  );

  const toggleFavorite = useCallback((jobId: string) => {
    const current = readFavorites();
    const exists = current.includes(jobId);
    const next = exists ? current.filter((id) => id !== jobId) : [...current, jobId];
    writeFavorites(next);
    setFavorites(next);
    return !exists; // true => ajouté, false => retiré
  }, []);

  const removeFavorite = useCallback((jobId: string) => {
    const current = readFavorites();
    if (!current.includes(jobId)) return;
    const next = current.filter((id) => id !== jobId);
    writeFavorites(next);
    setFavorites(next);
  }, []);

  const clearFavorites = useCallback(() => {
    writeFavorites([]);
    setFavorites([]);
  }, []);

  return {
    favorites,
    count: favorites.length,
    isFavorite,
    toggleFavorite,
    removeFavorite,
    clearFavorites,
  };
}
